/**
 * Query keys for React Query
 *
 * Provides a structured approach to defining and using query keys
 * which enables:
 * - Proper cache invalidation
 * - Consistent naming
 * - Type safety
 */

export const queryKeys = {
  // Projects related query keys
  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.projects.lists(), filters] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.projects.details(), id] as const,
    settings: (id: string) => [...queryKeys.projects.detail(id), 'settings'] as const,
  },

  // Stories related query keys
  stories: {
    all: ['stories'] as const,
    lists: () => [...queryKeys.stories.all, 'list'] as const,
    list: (projectId: string, filters: Record<string, unknown> = {}) =>
      [...queryKeys.stories.lists(), { projectId, ...filters }] as const,
    details: () => [...queryKeys.stories.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.stories.details(), id] as const,
    full: (id: string) => [...queryKeys.stories.all, 'full', id] as const,
    byProject: (projectId: string) =>
      [...queryKeys.stories.all, 'by-project', projectId] as const,
  },

  // Notes related query keys
  notes: {
    all: ['notes'] as const,
    lists: () => [...queryKeys.notes.all, 'list'] as const,
    list: () => [...queryKeys.notes.lists()] as const,
    details: () => [...queryKeys.notes.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.notes.details(), id] as const,
    byEntity: (entityId: string | number) =>
      [...queryKeys.notes.all, 'by-entity', entityId.toString()] as const,
    byFolder: (folderId: number) =>
      [...queryKeys.notes.all, 'by-folder', folderId] as const,
    folders: {
      all: () => [...queryKeys.notes.all, 'folders'] as const,
      list: () => [...queryKeys.notes.folders.all(), 'list'] as const,
      structure: (projectId?: string | number | null) =>
        projectId
          ? [...queryKeys.notes.folders.all(), 'structure', projectId.toString()] as const
          : [...queryKeys.notes.folders.all(), 'structure'] as const,
    },
  },
}